import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { BALANCE_TOKENS, BalanceToken, formatTokenAmount } from '../utils/balanceTokens';
import { formatSignedDecimal } from '../utils/numberFormat';

type BalanceHistoryPeriod = 'today' | 'yesterday' | '3d' | '7d' | '14d' | '21d' | '30d' | 'all';

type BalanceHistoryPoint = {
  token: string;
  totalAmount: string;
  recordedAt: string;
  timestampMs: number;
};

type BalanceHistoryResponse = {
  period: BalanceHistoryPeriod;
  from: string | null;
  to: string;
  points: BalanceHistoryPoint[];
};

const periods: Array<{ key: BalanceHistoryPeriod; label: string }> = [
  { key: 'today', label: 'Сегодня' },
  { key: 'yesterday', label: 'Вчера' },
  { key: '3d', label: '3д' },
  { key: '7d', label: '7д' },
  { key: '14d', label: '14д' },
  { key: '21d', label: '21д' },
  { key: '30d', label: '30д' },
  { key: 'all', label: 'Всё время' },
];

function formatChartTime(timestampMs: number, period: BalanceHistoryPeriod): string {
  const options: Intl.DateTimeFormatOptions = period === 'today' || period === 'yesterday'
    ? { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' }
    : { day: '2-digit', month: '2-digit', timeZone: 'Europe/Moscow' };
  return new Date(timestampMs).toLocaleString('ru-RU', options);
}

function BalanceHistoryChart({
  token,
  rows,
  period,
  from,
  to,
}: {
  token: BalanceToken;
  rows: BalanceHistoryPoint[];
  period: BalanceHistoryPeriod;
  from: string | null;
  to: string;
}) {
  const points = rows
    .map(row => ({ x: Number(row.timestampMs), y: Number(row.totalAmount) }))
    .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y));
  const width = 520;
  const height = 152;
  const padLeft = 58;
  const padRight = 12;
  const padTop = 12;
  const padBottom = 24;

  if (points.length === 0) {
    return (
      <section className="balance-history-chart">
        <div className="balance-history-chart-head">
          <strong>{token.label}</strong>
          <span className="text-muted">нет снимков за период</span>
        </div>
        <div className="balance-history-empty">История начнёт появляться после ближайшего снимка балансов</div>
      </section>
    );
  }

  const values = points.map(point => point.y);
  const actualMin = Math.min(...values);
  const actualMax = Math.max(...values);
  const padding = actualMax === actualMin ? Math.max(Math.abs(actualMax) * 0.005, 1) : (actualMax - actualMin) * 0.08;
  const min = actualMin - padding;
  const max = actualMax + padding;
  const xMin = from ? Date.parse(from) : points[0].x;
  const xMax = Math.max(Date.parse(to), xMin + 1);
  const xFor = (value: number) => padLeft + ((value - xMin) / (xMax - xMin)) * (width - padLeft - padRight);
  const yFor = (value: number) => padTop + ((max - value) / (max - min)) * (height - padTop - padBottom);
  const path = points.map(point => xFor(point.x).toFixed(2) + ',' + yFor(point.y).toFixed(2)).join(' ');
  const first = points[0].y;
  const last = points[points.length - 1].y;
  const delta = last - first;
  const deltaClass = delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral';
  const yTicks = [max, (max + min) / 2, min];

  return (
    <section className="balance-history-chart">
      <div className="balance-history-chart-head">
        <strong>{token.label}</strong>
        <span className="balance-history-values">
          {formatTokenAmount(token, String(first))} → {formatTokenAmount(token, String(last))}
          <span className={deltaClass}> {formatSignedDecimal(delta)}</span>
        </span>
      </div>
      <svg className="balance-history-svg" viewBox={'0 0 ' + width + ' ' + height} role="img" aria-label={'Изменение суммарного баланса ' + token.label}>
        <title>{'Баланс ' + token.label + ' за выбранный период'}</title>
        {yTicks.map((tick, index) => {
          const y = yFor(tick);
          return (
            <g key={index}>
              <line className="balance-history-chart-grid" x1={padLeft} x2={width - padRight} y1={y} y2={y} />
              <text className="balance-history-axis" x={4} y={y + 4}>{formatTokenAmount(token, String(tick))}</text>
            </g>
          );
        })}
        <text className="balance-history-axis" x={padLeft} y={height - 7}>{formatChartTime(xMin, period)}</text>
        <text className="balance-history-axis" x={width - padRight} y={height - 7} textAnchor="end">{formatChartTime(xMax, period)}</text>
        {points.length > 1 && <polyline className="balance-history-line" points={path} />}
        <circle className="balance-history-point" cx={xFor(points[points.length - 1].x)} cy={yFor(last)} r="3.5" />
      </svg>
    </section>
  );
}

export function BalanceHistoryDetails({ onClose }: { onClose: () => void }) {
  const [period, setPeriod] = useState<BalanceHistoryPeriod>('today');
  const [data, setData] = useState<BalanceHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.getBalanceHistory(period) as BalanceHistoryResponse;
        if (!cancelled) setData(response);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Не удалось загрузить историю балансов');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const timer = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [period]);

  const rowsByToken = useMemo(() => {
    const result: Record<string, BalanceHistoryPoint[]> = {};
    for (const point of data?.points || []) {
      result[point.token] = [...(result[point.token] || []), point];
    }
    return result;
  }, [data]);

  return (
    <div className="balance-history-overlay" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <div className="balance-history-dialog" role="dialog" aria-modal="true" aria-labelledby="balance-history-title">
        <div className="balance-history-head">
          <div>
            <h2 id="balance-history-title">История балансов</h2>
            <span>Сумма по TON wallet и всем биржам · UTC+3</span>
          </div>
          <button type="button" className="btn btn-sm btn-outline" onClick={onClose}>Закрыть</button>
        </div>
        <div className="balance-history-periods" aria-label="Период истории балансов">
          {periods.map(item => (
            <button
              type="button"
              key={item.key}
              className={'balance-history-period' + (period === item.key ? ' active' : '')}
              onClick={() => setPeriod(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        {loading && !data && <div className="balance-history-loading">Загрузка...</div>}
        {error && <div className="balance-history-error">{error}</div>}
        <div className="balance-history-grid">
          {BALANCE_TOKENS.map(token => (
            <BalanceHistoryChart
              key={token.id}
              token={token}
              rows={rowsByToken[token.id] || []}
              period={period}
              from={data?.from || null}
              to={data?.to || new Date().toISOString()}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
